import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrefetchClinicData } from './useClinicData';

/**
 * Custom hook for optimized navigation with prefetching
 * Prefetches data when user hovers over navigation elements
 */
export const useOptimizedNavigation = () => {
  const navigate = useNavigate();
  const { prefetchServices, prefetchTestimonials, prefetchAll } =
    usePrefetchClinicData();

  const navigateWithPrefetch = useCallback(
    (
      path: string,
      clinicSlug: string,
      prefetchData: 'services' | 'testimonials' | 'all' | 'none' = 'none'
    ) => {
      // Prefetch data based on the target page
      switch (prefetchData) {
        case 'services':
          prefetchServices(clinicSlug);
          break;
        case 'testimonials':
          prefetchTestimonials(clinicSlug);
          break;
        case 'all':
          prefetchAll(clinicSlug);
          break;
        default:
          // No prefetching
          break;
      }

      // Navigate to the target page
      navigate(path);
    },
    [navigate, prefetchServices, prefetchTestimonials, prefetchAll]
  );

  const handleMouseEnter = useCallback(
    (
      clinicSlug: string,
      prefetchData: 'services' | 'testimonials' | 'all' = 'all'
    ) => {
      // Prefetch data on hover for faster navigation
      switch (prefetchData) {
        case 'services':
          prefetchServices(clinicSlug);
          break;
        case 'testimonials':
          prefetchTestimonials(clinicSlug);
          break;
        case 'all':
          prefetchAll(clinicSlug);
          break;
      }
    },
    [prefetchServices, prefetchTestimonials, prefetchAll]
  );

  return {
    navigateWithPrefetch,
    handleMouseEnter,
  };
};



